import { useEffect, useState } from 'react'
import { api } from '@/lib/api'
import type { BidMemberInfo } from '@/components/ui/owner-assignment'

// Every mounted `useBidMembers(bidId)` listens here, so a change made from one
// surface (an @mention that adds a colleague) reaches every other surface on
// the page showing the same bid.
const refreshListeners = new Set<(bidId: string) => void>()

/** Ask every mounted `useBidMembers` for this bid to refetch. */
export function refreshBidMembers(bidId: string): void {
  for (const listener of refreshListeners) listener(bidId)
}

/**
 * The bid's members, for assignment pickers, @mention autocomplete and
 * commenter avatars.
 *
 * Every editor page needs the same list, so the fetch lives here rather than
 * being copied into each one. Failures are swallowed on purpose: the surfaces
 * that consume this all degrade gracefully to an empty list (no suggestions,
 * initials-only avatars) and none of them is worth failing a page over.
 */
export function useBidMembers(bidId: string | undefined): BidMemberInfo[] {
  const [members, setMembers] = useState<BidMemberInfo[]>([])
  const [version, setVersion] = useState(0)

  useEffect(() => {
    if (!bidId) return
    const listener = (changed: string) => {
      if (changed === bidId) setVersion((v) => v + 1)
    }
    refreshListeners.add(listener)
    return () => {
      refreshListeners.delete(listener)
    }
  }, [bidId])

  useEffect(() => {
    if (!bidId) return
    let cancelled = false
    void api
      .get<{ members: BidMemberInfo[] }>(`/bids/${bidId}/members`)
      .then((data) => {
        if (!cancelled) setMembers(data.members)
      })
      .catch(() => {
        /* non-critical */
      })
    return () => {
      cancelled = true
    }
  }, [bidId, version])

  return members
}
